import React, { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { Device } from 'mediasoup-client';
import { RtpCapabilities, Transport as MediasoupTransport, Consumer } from 'mediasoup-client/lib/types';
import { useAuth } from '../contexts/AuthContext';
import useServerUrl from '../hooks/useServerUrl';
import configService from '../services/config.service';
import VideoStream from '../VideoStream';

interface DeviceCapabilities {
    resolutions?: Array<{ width: number; height: number }>;
    frameRates?: number[];
    codecs?: string[];
    audioSupport?: boolean;
    motionDetection?: boolean;
    panTiltZoom?: boolean;
    nightVision?: boolean;
    infrared?: boolean;
}

interface DeviceNetworkInfo {
    macAddress?: string;
    networkInterface?: string;
    subnet?: string;
    gateway?: string;
    dns?: string[];
    connectionType?: 'wired' | 'wireless';
    signalStrength?: number;
}

interface DeviceStatus {
    firmwareVersion?: string;
    hardwareVersion?: string;
    uptime?: number;
    temperature?: number;
    memoryUsage?: number;
    cpuUsage?: number;
    diskUsage?: number;
    lastReboot?: number;
}

interface StreamMetrics {
    bitrate?: number;
    packetLoss?: number;
    latency?: number;
    jitter?: number;
    frameRate?: number;
    resolution?: { width: number; height: number };
    codec?: string;
    lastMetricsUpdate?: number;
}

interface DeviceSecurity {
    authenticationMethods?: string[];
    encryptionSupport?: string[];
    certificateInfo?: {
        issuer?: string;
        subject?: string;
        validFrom?: number;
        validTo?: number;
    };
    accessLevel?: 'admin' | 'operator' | 'user';
}

interface DeviceInfo {
    urn: string;
    ipAddress: string;
    hostname: string;
    port: number;
    model: string;
    manufacturer?: string;
    serialNumber?: string;
    lastSeen: number;
    isOnline: boolean;
    rtspUrl?: string;

    // Enhanced device information
    capabilities?: DeviceCapabilities;
    networkInfo?: DeviceNetworkInfo;
    status?: DeviceStatus;
    streamMetrics?: StreamMetrics;
    security?: DeviceSecurity;

    // Discovery metadata
    discoveryMethod?: 'onvif' | 'manual' | 'imported';
    firstDiscovered?: number;
    lastVerified?: number;
    verificationFailures?: number;
}

interface ProducerInfo {
    producerId: string;
    appData: { rtspUrl?: string; streamName?: string };
    deviceInfo?: DeviceInfo;
}

interface StreamData {
    producerId: string; // Use producerId as the unique identifier
    stream: MediaStream;
    consumer: Consumer;
    appData?: { rtspUrl?: string; streamName?: string };
    deviceInfo?: DeviceInfo;
}

const socketRequest = <T,>(socket: Socket, type: string, data = {}): Promise<T> => {
    return new Promise((resolve, reject) => {
        socket.emit(type, data, (response: T | { error: string }) => {
            if (response && (response as { error: string }).error) {
                reject((response as { error: string }).error);
            } else {
                resolve(response as T);
            }
        });
    });
};

interface VideoViewerProps {
    locationKey?: string;
}

const VideoViewer: React.FC<VideoViewerProps> = ({ locationKey }) => {
    const { user, token, logout } = useAuth();
    const SERVER_URL = useServerUrl(); // Runtime config
    const [status, setStatus] = useState('Connecting...');
    const [streams, setStreams] = useState<StreamData[]>([]);

    const deviceRef = useRef<Device | null>(null);
    const recvTransportRef = useRef<MediasoupTransport | null>(null);
    const socketRef = useRef<Socket | null>(null);
    const consumersRef = useRef<Consumer[]>([]);
    const streamsRef = useRef<StreamData[]>([]);

    // Keep streamsRef in sync with streams state
    useEffect(() => {
        streamsRef.current = streams;
    }, [streams]);

    const consumeStream = React.useCallback(async (producerInfo: ProducerInfo) => {
        const socket = socketRef.current;
        const device = deviceRef.current;
        const transport = recvTransportRef.current;
        
        if (!socket || !socket.connected) {
            console.error('Cannot consume stream: socket not connected.');
            return;
        }
        
        if (!device || !transport) {
            console.error('Cannot consume stream: device or transport not ready.');
            return;
        }
        
        console.log(`[App] Consuming stream for producer: ${JSON.stringify(producerInfo)}`);
        const { producerId, appData, deviceInfo } = producerInfo;
        console.log(`producerId, appData`, producerId, appData);
        console.log(`[App] deviceInfo received:`, deviceInfo);
        
        if (streamsRef.current.some(s => s.producerId === producerId)) {
            console.log(`[App] Stream ${producerId} is already being consumed.`);
            return;
        }

        console.log(`[App] Attempting to consume stream for producer: ${producerId.substring(0, 8)}`);

        try {
            const consumerParams = await socketRequest<{
                id: string;
                producerId: string;
                kind: 'audio' | 'video';
                rtpParameters: any;
            }>(socket, 'consume', {
                rtpCapabilities: device.rtpCapabilities,
                producerId
            });

            console.log(`[App] Received consumer params for ${producerId.substring(0, 8)}:`, consumerParams);

            const consumer = await transport.consume({
                id: consumerParams.id,
                producerId: consumerParams.producerId,
                kind: consumerParams.kind,
                rtpParameters: consumerParams.rtpParameters,
            });

            const stream = new MediaStream();
            stream.addTrack(consumer.track);
            consumersRef.current.push(consumer);

            console.log(`[App] Consumer created for producer ${producerId.substring(0, 8)}, adding to streams`);

            // Add stream immediately to UI for faster feedback
            setStreams(prev => [...prev, { producerId, stream, consumer, appData, deviceInfo }]);
            console.log(`[App] Added stream to UI with deviceInfo:`, deviceInfo);

            // Resume consumer to start receiving media
            socket.emit('resume', { consumerId: consumer.id });
            console.log(`[App] Sent resume for consumer ${consumer.id}`);

            // Add consumer event listeners for debugging
            consumer.on('transportclose', () => {
                console.log(`[App] Consumer ${consumer.id} transport closed`);
                setStreams(prev => prev.filter(s => s.producerId !== producerId));
            });

            consumer.on('trackended', () => {
                console.log(`[App] Consumer ${consumer.id} track ended`);
                setStreams(prev => prev.filter(s => s.producerId !== producerId));
            });

            consumer.on('@close', () => {
                console.log(`[App] Consumer ${consumer.id} closed`);
                setStreams(prev => prev.filter(s => s.producerId !== producerId));
            });
        } catch (error) {
            console.error(`Error consuming stream for producer ${producerId}:`, error);
        }
    }, []);

    useEffect(() => {
        // Only connect if we have location key
        if (!locationKey) {
            setStatus('Location key required');
            return;
        }

        const socket = io(SERVER_URL, {
            auth: {
                locationKey: locationKey
            }
        });
        socketRef.current = socket;

        const initialize = () => {
            socket.on('connect', async () => {
                try {
                    console.log(`[App] Connected to server at ${SERVER_URL}`);
                    setStatus('Connected. Initializing...');
                    
                    // Reset all refs to ensure fresh state
                    deviceRef.current = null;
                    recvTransportRef.current = null;
                    consumersRef.current = [];
                    streamsRef.current = [];
                    setStreams([]);
                    
                    const routerRtpCapabilities = await socketRequest<RtpCapabilities>(socket, 'getRouterRtpCapabilities');
                    console.log('[App] Got Router RTP Capabilities:', routerRtpCapabilities);

                    const device = new Device();
                    deviceRef.current = device;

                    console.log('[App] Loading device...');

                    await device.load({ routerRtpCapabilities });
                    console.log('[App] Device loaded successfully.');

                    setStatus('Setting up connection...');
                    const transportParams = await socketRequest<{ params: any }>(socket, 'createWebRtcTransport', {});

                    // Get ICE servers from strongly-typed configuration
                    const iceServers = configService.getIceServers();
                    console.log('[App] Configured ICE servers:', iceServers);

                    // Add ICE servers to transport parameters
                    const enhancedTransportParams = {
                        ...transportParams.params,
                        iceServers,
                    };

                    const transport = device.createRecvTransport(enhancedTransportParams);
                    recvTransportRef.current = transport;
                    console.log('[App] Receive transport created with ICE servers:', transport);

                    transport.on('connect', ({ dtlsParameters }, callback, errback) => {
                        console.log('[App] Transport connect event');
                        socketRequest(socket, 'connectTransport', { transportId: transport.id, dtlsParameters })
                            .then(callback)
                            .catch(errback);
                    });

                    transport.on('connectionstatechange', (state) => {
                        console.log(`[App] Transport connection state changed: ${state}`);
                        if (state === 'connected') {
                            setStatus('Connected. Ready to view cameras.');
                        }
                    });

                    transport.on('icecandidateerror', (error) => {
                        console.error('[App] ICE candidate error:', error);
                    });

                    transport.on('icegatheringstatechange', (state) => {
                        console.log('[App] Transport ICE gathering state changed:', state);
                    });

                    const producers = await socketRequest<ProducerInfo[]>(socket, 'getProducers');
                    console.log(`[App] Found existing producers:`, producers);
                    producers.forEach(consumeStream);

                } catch (err) {
                    console.error('[App] Initialization failed inside connect handler:', err);
                    setStatus(`Error: ${(err as Error).message}`);
                }
            });

            socket.on('newProducer', (producerInfo: ProducerInfo) => {
                console.log(`[App] newProducer event received:`, producerInfo);
                consumeStream(producerInfo);
            });

            socket.on('producerClosed', ({ producerId }: { producerId: string }) => {
                console.log(`[App] producerClosed event received: ${producerId.substring(0, 8)}`);
                setStreams(prev => {
                    const streamToClose = prev.find(s => s.producerId === producerId);
                    if (streamToClose) {
                        console.log(`[App] Closing consumer for producer ${producerId.substring(0, 8)}`);
                        streamToClose.consumer.close();
                        consumersRef.current = consumersRef.current.filter(c => c.id !== streamToClose.consumer.id);
                    }
                    return prev.filter(s => s.producerId !== producerId);
                });
            });

            socket.on('disconnect', () => {
                console.log('[App] Disconnected from server.');
                setStatus('Disconnected.');
                setStreams([]);
            });
        };

        initialize();

        return () => {
            console.log('[App] Cleanup: Disconnecting socket and closing all connections.');
            
            // Close all consumers
            consumersRef.current.forEach(c => {
                try {
                    c.close();
                } catch (e) {
                    console.error('Error closing consumer:', e);
                }
            });
            consumersRef.current = [];
            
            // Close transport
            if (recvTransportRef.current) {
                try {
                    recvTransportRef.current.close();
                } catch (e) {
                    console.error('Error closing transport:', e);
                }
                recvTransportRef.current = null;
            }
            
            // Reset device
            deviceRef.current = null;
            
            // Clear streams
            streamsRef.current = [];
            setStreams([]);
            
            // Disconnect socket last
            socket.disconnect();
            socketRef.current = null;
            
            console.log('[App] Cleanup complete.');
        };
    }, [consumeStream, locationKey]);

    return (
        <div className="app-container">
            <div className="video-grid">
                {streams.length > 0 ? (
                    streams.map(({ producerId, stream, appData, deviceInfo }) => (
                        <VideoStream
                            key={producerId}
                            producerId={producerId}
                            stream={stream}
                            streamName={appData?.streamName}
                            deviceInfo={deviceInfo}
                        />
                    ))
                ) : (
                    <div className="no-streams-message">
                        <p>Waiting for streams...</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VideoViewer;
